import { supabase } from "@/lib/supabase";
import { Persona } from "@/lib/personas";

interface SaveAccountResult {
  success: boolean;
  message: string;
  quotaExceeded?: boolean;
  currentPlan?: string;
}

/**
 * Saves an Instagram account to the user's saved accounts list
 */
export async function saveInstagramAccount(
  username: string,
  profileData: any,
  persona?: Persona,
  profilePicUrl?: string,
  profilePicUrlHD?: string
): Promise<SaveAccountResult> {
  if (!username) {
    return {
      success: false,
      message: "Username is required",
    };
  }

  try {
    console.log("계정 저장 시도:", username);
    console.log("프로필 데이터 타입:", typeof profileData);
    console.log(
      "프로필 데이터 내용:",
      profileData
        ? JSON.stringify(profileData).substring(0, 100) + "..."
        : "null"
    );
    if (persona) {
      console.log("페르소나:", persona.id, persona.name);
    }

    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.user) {
      throw new Error("User not authenticated");
    }

    // 계정 쿼터 확인 (새로운 방식)
    try {
      // user_chat_credits 테이블에서 account_quota 확인
      const { data: quotaData, error: quotaError } = await supabase
        .from('user_chat_credits')
        .select('account_quota')
        .eq('user_id', sessionData.session.user.id)
        .single();

      // 에러가 있고 레코드가 없는 경우가 아니라면 (실제 에러면) 처리
      if (quotaError && quotaError.code !== 'PGRST116') {
        console.warn("계정 쿼터 체크 중 에러 발생:", quotaError);
      }
      
      // 쿼터가 -1이 아니고(무제한이 아니고) 쿼터 레코드가 있는 경우
      if (quotaData && quotaData.account_quota !== -1) {
        // 현재 연결된 계정 수 확인
        const { count, error: countError } = await supabase
          .from('saved_instagram_accounts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', sessionData.session.user.id)
          .is('deleted_at', null);
          
        if (countError) {
          console.error("연결된 계정 수 확인 중 오류:", countError);
        } else if (count !== null && count >= quotaData.account_quota) {
          // 쿼터 초과
          return {
            success: false,
            message: `계정 연결 쿼터를 초과했습니다. 현재 쿼터: ${quotaData.account_quota}개`,
            quotaExceeded: true,
            currentPlan: quotaData.account_quota === 10 ? 'basic' : 'free'
          };
        }
      }
    } catch (quotaCheckError) {
      console.error("쿼터 체크 중 예외 발생:", quotaCheckError);
      // 쿼터 체크 실패해도 계속 진행 (데이터베이스 트리거로 2차 검증)
    }

    // 최근 게시물이 있으면 첫 번째 포스트 URL 저장
    const latestPostUrl = profileData?.latestPosts?.[0]?.displayUrl || null;

    // Prepare data for insertion
    const accountData: any = {
      user_id: sessionData.session.user.id,
      username,
      profile_data: profileData,
      supabase_profile_pic_url: profilePicUrl || null,
      supabase_profile_hd_pic_url: profilePicUrlHD || null,
      supabase_latest_post_url: latestPostUrl,
      last_data_update: new Date().toISOString()
    };

    // Add persona if provided
    if (persona) {
      accountData.persona_id = persona.id;
      accountData.persona_name = persona.name;
    }

    // Upsert into Supabase (update if exists, insert if not)
    const { data: upsertData, error: upsertError } = await supabase
      .from("saved_instagram_accounts")
      .upsert([accountData], { onConflict: "user_id,username" })
      .select("*");

    if (upsertError) {
      console.error("계정 저장/업데이트 실패:", upsertError);
      
      // 계정 쿼터 초과 오류 특수 처리
      if (upsertError.message.includes('account_quota_exceeded')) {
        // 플랜 정보 추출 시도
        const planMatch = upsertError.message.match(/현재 쿼터: (\d+|무제한)/);
        const currentPlan = planMatch ? 
          (planMatch[1] === '2' ? 'free' : 
           planMatch[1] === '10' ? 'basic' : 'premium') : 'free';
           
        return {
          success: false,
          message: "계정 연결 쿼터를 초과했습니다. 요금제를 업그레이드하세요.",
          quotaExceeded: true,
          currentPlan
        };
      }
      
      throw upsertError; // Throw other errors
    }

    console.log("계정 저장/업데이트 성공:", username, upsertData?.[0]?.id);
    return {
      success: true,
      message: "Account saved/updated successfully",
    };
  } catch (error: any) {
    console.error("Error saving Instagram account:", error);
    
    // 계정 쿼터 초과 오류 특수 처리
    if (error.message && error.message.includes('account_quota_exceeded')) {
      const planMatch = error.message.match(/현재 쿼터: (\d+|무제한)/);
      const currentPlan = planMatch ? 
        (planMatch[1] === '2' ? 'free' : 
         planMatch[1] === '10' ? 'basic' : 'premium') : 'free';
         
      return {
        success: false,
        message: "계정 연결 쿼터를 초과했습니다. 요금제를 업그레이드하세요.",
        quotaExceeded: true,
        currentPlan
      };
    }
    
    return {
      success: false,
      message: error.message || "Failed to save account",
    };
  }
}

/**
 * Deletes a saved Instagram account
 */
export async function deleteSavedAccount(
  id: string
): Promise<SaveAccountResult> {
  try {
    console.log("계정 삭제 시도:", id);

    // Get current user
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.user) {
      throw new Error("User not authenticated");
    }

    const userId = sessionData.session.user.id;

    // 실제 삭제 대신 deleted_at 필드 업데이트 (소프트 삭제)
    const { data, error } = await supabase
      .from("saved_instagram_accounts")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id)
      .eq("user_id", userId)
      .select();

    if (error) {
      console.error("계정 삭제 실패:", error);
      // Check for RLS violation specifically
      if (error.code === "42501") {
        return {
          success: false,
          message: "이 계정을 삭제할 권한이 없습니다",
        };
      }
      throw error;
    }

    // Check if any rows were actually updated
    if (!data || data.length === 0) {
      return {
        success: false,
        message: "계정을 찾을 수 없거나 삭제할 권한이 없습니다",
      };
    }

    console.log("계정 삭제 성공:", id);
    return {
      success: true,
      message: "계정이 성공적으로 삭제되었습니다",
    };
  } catch (error: any) {
    console.error("계정 삭제 중 오류:", error);
    return {
      success: false,
      message: error.message || "계정 삭제 실패",
    };
  }
}

/**
 * Gets a single saved Instagram account by username
 */
export async function getSavedAccount(username: string) {
  try {
    console.log("저장된 계정 조회:", username);
    const { data, error } = await supabase
      .from("saved_instagram_accounts")
      .select("*")
      .eq("username", username)
      .is("deleted_at", null)
      .maybeSingle();

    if (error) {
      console.error("계정 조회 실패:", error);
      throw error;
    }

    console.log("계정 조회 결과:", data ? "데이터 있음" : "데이터 없음");
    return data;
  } catch (error) {
    console.error("Error fetching saved account:", error);
    return null;
  }
}

/**
 * 공개 인스타그램 계정을 사용자의 저장된 계정으로 복사
 */
export async function copyPublicAccountToSaved(accountId: string): Promise<SaveAccountResult> {
  try {
    console.log("공개 계정 복사 시도:", accountId);

    // 현재 사용자 세션 확인
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session?.user) {
      return {
        success: false,
        message: "로그인이 필요합니다",
      };
    }

    const userId = sessionData.session.user.id;
    
    // 계정 쿼터 확인 (새로운 방식)
    try {
      // user_chat_credits 테이블에서 account_quota 확인
      const { data: quotaData, error: quotaError } = await supabase
        .from('user_chat_credits')
        .select('account_quota')
        .eq('user_id', userId)
        .single();

      // 쿼터가 -1이 아니고(무제한이 아니고) 쿼터 레코드가 있는 경우
      if (!quotaError && quotaData && quotaData.account_quota !== -1) {
        // 현재 연결된 계정 수 확인
        const { count, error: countError } = await supabase
          .from('saved_instagram_accounts')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', userId)
          .is('deleted_at', null);
          
        if (!countError && count !== null && count >= quotaData.account_quota) {
          // 쿼터 초과
          return {
            success: false,
            message: `계정 연결 쿼터를 초과했습니다. 현재 쿼터: ${quotaData.account_quota}개`,
            quotaExceeded: true,
            currentPlan: quotaData.account_quota === 10 ? 'basic' : 'free'
          };
        }
      }
    } catch (quotaCheckError) {
      console.error("쿼터 체크 중 예외 발생:", quotaCheckError);
      // 쿼터 체크 실패해도 계속 진행 (데이터베이스 트리거로 2차 검증)
    }

    // 복사할 공개 계정 정보 조회
    const { data: publicAccount, error: fetchError } = await supabase
      .from("public_instagram_accounts")
      .select("*")
      .eq("id", accountId)
      .single();

    if (fetchError) {
      console.error("공개 계정 조회 실패:", fetchError);
      return {
        success: false,
        message: "계정 정보를 불러올 수 없습니다",
      };
    }

    if (!publicAccount) {
      return {
        success: false,
        message: "계정을 찾을 수 없습니다",
      };
    }

    // 중복 계정이 있는지 확인
    const { data: existingAccount } = await supabase
      .from("saved_instagram_accounts")
      .select("id")
      .eq("user_id", sessionData.session.user.id)
      .eq("username", publicAccount.username)
      .is("deleted_at", null)
      .maybeSingle();

    // Process profile data to ensure valid JSON
    let processedProfileData = null;
    if (publicAccount.profile_data) {
      try {
        // If it's already a string, try to parse and re-stringify to validate
        if (typeof publicAccount.profile_data === 'string') {
          processedProfileData = JSON.parse(publicAccount.profile_data);
        } else {
          // Otherwise, assume it's an object and use as is
          processedProfileData = publicAccount.profile_data;
        }
      } catch (e) {
        console.error("프로필 데이터 처리 오류:", e);
        // Keep processedProfileData as null
      }
    }

    // 저장할 계정 데이터 준비
    const accountData = {
      user_id: sessionData.session.user.id,
      username: publicAccount.username,
      profile_data: processedProfileData,
      supabase_profile_pic_url: publicAccount.supabase_profile_pic_url,
      supabase_profile_hd_pic_url: publicAccount.supabase_profile_hd_pic_url,
      supabase_latest_post_url: publicAccount.supabase_latest_post_url,
      persona_id: publicAccount.persona_id,
      persona_name: publicAccount.persona_name,
      display_name: publicAccount.display_name,
      profile_website: publicAccount.profile_website,
      business_category: publicAccount.business_category,
      profile_color: publicAccount.profile_color,
      profile_theme: publicAccount.profile_theme || 'default',
    };

    // 로컬 스토리지에 프로필 데이터 저장 (빠른 접근용)
    if (processedProfileData) {
      try {
        localStorage.setItem(
          `profile-${publicAccount.username}`,
          JSON.stringify(processedProfileData)
        );
      } catch (e) {
        console.error("로컬 스토리지 저장 오류:", e);
        // 로컬 스토리지 저장 실패해도 계속 진행
      }
    }

    console.log("Upserting account data with profile_data type:", typeof accountData.profile_data);

    // 새 계정 저장 또는 기존 계정 업데이트
    const { error: saveError } = await supabase
      .from("saved_instagram_accounts")
      .upsert([accountData], {
        onConflict: "user_id,username",
      });

    if (saveError) {
      console.error("계정 저장 실패:", saveError);
      
      // 계정 쿼터 초과 오류 특수 처리
      if (saveError.message.includes('account_quota_exceeded')) {
        // 플랜 정보 추출 시도
        const planMatch = saveError.message.match(/현재 쿼터: (\d+|무제한)/);
        const currentPlan = planMatch ? 
          (planMatch[1] === '2' ? 'free' : 
           planMatch[1] === '10' ? 'basic' : 'premium') : 'free';
           
        return {
          success: false,
          message: "계정 연결 쿼터를 초과했습니다. 요금제를 업그레이드하세요.",
          quotaExceeded: true,
          currentPlan
        };
      }
      
      return {
        success: false,
        message: saveError.message || "계정을 저장하는데 실패했습니다",
      };
    }

    return {
      success: true,
      message: existingAccount ? "계정이 업데이트되었습니다" : "계정이 저장되었습니다",
    };
  } catch (error: any) {
    console.error("공개 계정 복사 중 오류:", error);
    return {
      success: false,
      message: error.message || "계정 복사 중 오류가 발생했습니다",
    };
  }
}